import { useEffect, useState } from 'react';
import {
  Autocomplete,
  Button,
  Chip,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { payeesApi, type NameOnLineScope, type Payee, type PayeeKind } from '@/api/payees';
import { SubmitButton } from '@/components/SubmitButton';

export type NamePayeeResult =
  { payeeId: number; scope: NameOnLineScope } | { name: string; scope: NameOnLineScope };

interface NamePayeeFormProps {
  bankText: string;
  titleId: string;
  busy?: boolean;
  /** Focus the title as the form appears (it replaced the host's content in place). */
  focusTitle?: boolean;
  /** The host routes Cancel through its unsaved-work guard. */
  onCancel: () => void;
  onSave: (body: NamePayeeResult) => void;
  /** A pick, a typed name or a changed scope is unsaved work; reported to the host's guard. */
  onDirtyChange: (dirty: boolean) => void;
}

/**
 * Name / change a counterparty on a line (TMMAPP-0097 §C 22, from `NamePayeeDialog`): pick an
 * existing payee or type a new name, plus LINE vs GROUP scope (366-day helper). The title, fields
 * and actions of a dialog, so a host renders it in a `Dialog` (payee pages) or in place of a
 * drawer's content (the transaction drawer). It starts clean each time it mounts.
 */
export function NamePayeeForm({
  bankText,
  titleId,
  busy = false,
  focusTitle = false,
  onCancel,
  onSave,
  onDirtyChange,
}: NamePayeeFormProps) {
  const { t } = useTranslation();
  const [q, setQ] = useState('');
  const [options, setOptions] = useState<Payee[]>([]);
  const [selected, setSelected] = useState<Payee | null>(null);
  const [freeName, setFreeName] = useState('');
  const [scope, setScope] = useState<NameOnLineScope>('LINE');

  useEffect(() => {
    let cancelled = false;
    const handle = window.setTimeout(() => {
      void payeesApi
        .list({
          ...(q.trim().length > 0 ? { q: q.trim() } : {}),
          status: 'ACTIVE',
        })
        .then((rows) => {
          if (!cancelled) setOptions(rows);
        })
        .catch(() => {
          if (!cancelled) setOptions([]);
        });
    }, 300);
    return () => {
      cancelled = true;
      window.clearTimeout(handle);
    };
  }, [q]);

  const dirty = selected !== null || freeName.length > 0 || scope !== 'LINE';
  useEffect(() => {
    onDirtyChange(dirty);
  }, [dirty, onDirtyChange]);
  // Gone, the form holds nothing: the host's guard is clean again.
  useEffect(() => () => onDirtyChange(false), [onDirtyChange]);

  useEffect(() => {
    if (focusTitle) document.getElementById(titleId)?.focus();
  }, [focusTitle, titleId]);

  const nameReady = selected !== null || freeName.trim().length > 0;

  function handleSave() {
    if (selected !== null) {
      onSave({ payeeId: selected.id, scope });
      return;
    }
    const name = freeName.trim();
    if (name.length === 0) return;
    onSave({ name, scope });
  }

  function kindLabel(kind: PayeeKind): string {
    return t(`payee.kind.${kind}`);
  }

  return (
    <>
      <DialogTitle id={titleId} tabIndex={-1}>
        {t('payee.nameThis')}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Typography variant="mono" sx={{ color: 'text.secondary' }}>
            {t('payee.bankText', { text: bankText })}
          </Typography>
          <Autocomplete
            options={options}
            getOptionLabel={(option) => option.name}
            value={selected}
            onChange={(_event, value) => {
              setSelected(value);
              if (value !== null) setFreeName('');
            }}
            onInputChange={(_event, value, reason) => {
              if (reason === 'input') {
                setQ(value);
                setFreeName(value);
                setSelected(null);
              }
            }}
            renderOption={(props, option) => (
              <li {...props} key={option.id}>
                <Stack direction="row" sx={{ gap: 1, alignItems: 'center', width: '100%' }}>
                  <span>{option.name}</span>
                  <Chip size="small" label={kindLabel(option.kind)} />
                </Stack>
              </li>
            )}
            renderInput={(params) => <TextField {...params} label={t('payee.name')} />}
            freeSolo={false}
          />
          <FormControl>
            <FormLabel id={`${titleId}-scope`}>{t('payee.scopeLine')}</FormLabel>
            <RadioGroup
              aria-labelledby={`${titleId}-scope`}
              value={scope}
              onChange={(event) => setScope(event.target.value as NameOnLineScope)}
            >
              <FormControlLabel value="LINE" control={<Radio />} label={t('payee.scopeLine')} />
              <FormControlLabel
                value="GROUP"
                control={<Radio />}
                label={
                  <Stack spacing={0}>
                    <span>{t('payee.scopeGroup')}</span>
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                      {t('payee.scopeGroupHelp')}
                    </Typography>
                  </Stack>
                }
              />
            </RadioGroup>
          </FormControl>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel}>{t('common.cancel')}</Button>
        <SubmitButton onClick={handleSave} disabled={!nameReady || busy} loading={busy}>
          {t('common.save')}
        </SubmitButton>
      </DialogActions>
    </>
  );
}
